import type { ReactNode } from 'react'
import { cn } from '../cn'
import { statusTone, type Tone } from '../status'

export interface TimelineItem {
  id: string
  /** Pre-formatted date line ("7 Oct 2026"). */
  date: ReactNode
  /** Secondary line under the date ("Written response"). */
  dateNote?: ReactNode
  title: ReactNode
  body?: ReactNode
  meta?: ReactNode
  /** Marker colour; derive from a status with `statusTone()`. */
  tone?: Tone
  /** Convenience: a status value whose tone colours the marker. */
  status?: string
}

export interface TimelineProps {
  items: TimelineItem[]
  /** Accessible name ("Engagement record"). */
  label?: string
  className?: string
}

const MARKER: Record<Tone, string> = {
  positive: 'bg-positive',
  attention: 'bg-attention',
  watch: 'bg-watch',
  quiet: 'bg-teal-300',
  info: 'bg-teal',
}

/** Dated, newest-first record (correspondence, meetings, participation history). */
export function Timeline({ items, label, className }: TimelineProps) {
  return (
    <ol aria-label={label} className={cn('relative', className)}>
      {items.map((item, index) => {
        const tone = item.tone ?? (item.status ? statusTone(item.status) : 'info')
        const last = index === items.length - 1
        return (
          <li
            key={item.id}
            className="relative grid gap-x-6 gap-y-1 pb-7 pl-7 sm:grid-cols-[8.5rem_1fr]"
          >
            {!last ? (
              <span
                aria-hidden="true"
                className="absolute top-3 bottom-0 left-[5px] w-px bg-line"
              />
            ) : null}
            <span
              aria-hidden="true"
              className={cn(
                'absolute top-[7px] left-0 size-[11px] rounded-full ring-[3px] ring-surface',
                MARKER[tone],
              )}
            />
            <div className="text-[13px] leading-relaxed text-sage">
              <div className="font-medium text-ink-soft">{item.date}</div>
              {item.dateNote ? <div className="text-xs">{item.dateNote}</div> : null}
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold leading-snug text-ink">{item.title}</div>
              {item.body ? (
                <div className="mt-2 text-sm leading-relaxed text-ink-soft">{item.body}</div>
              ) : null}
              {item.meta ? <div className="mt-2 text-xs text-sage">{item.meta}</div> : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
